/**
 * GeometryBase - Abstract base class for all geometry types
 *
 * All geometry objects (Point, Curve, Surface, Solid) inherit from this class.
 * Provides common ID generation, type identification, and attribute management.
 */

let idCounter = 0;

/**
 * Generate a unique ID with type prefix
 * @param {string} prefix - Type prefix (pt, crv, srf, sol)
 * @returns {string} Unique ID
 */
export function generateId(prefix = 'geom') {
  return `${prefix}_${Date.now()}_${++idCounter}`;
}

/**
 * Geometry types enum
 */
export const GeometryType = {
  // Points
  POINT: 'Point',

  // Curves
  LINE: 'Line',
  POLYLINE: 'Polyline',
  ARC: 'Arc',
  CIRCLE: 'Circle',
  RECTANGLE: 'Rectangle',
  POLYGON: 'Polygon',
  NURBS_CURVE: 'NurbsCurve',

  // Surfaces
  PLANAR_SURFACE: 'PlanarSurface',
  EXTRUDED_SURFACE: 'ExtrudedSurface',
  REVOLVED_SURFACE: 'RevolvedSurface',
  NURBS_SURFACE: 'NurbsSurface',

  // Solids
  BOX: 'Box',
  CYLINDER: 'Cylinder',
  SPHERE: 'Sphere',
  EXTRUSION: 'Extrusion',
  REVOLVE: 'Revolve',
  SWEEP: 'Sweep',
  LOFT: 'Loft',
  BREP: 'Brep',
};

/**
 * Default attributes for geometry objects
 */
export const defaultAttributes = () => ({
  name: '',
  layer: 'default',
  color: null, // null = use layer color
  visible: true,
  locked: false,
});

/**
 * Base class for all geometry objects
 */
export class GeometryBase {
  /**
   * @param {string} type - Geometry type from GeometryType enum
   * @param {string} idPrefix - ID prefix for this geometry type
   * @param {Object} attributes - Optional attributes
   */
  constructor(type, idPrefix = 'geom', attributes = {}) {
    this.id = generateId(idPrefix);
    this.type = type;
    this.attributes = { ...defaultAttributes(), ...attributes };
    this.createdAt = Date.now();
    this.modifiedAt = Date.now();
  }

  /**
   * Update modification timestamp
   */
  touch() {
    this.modifiedAt = Date.now();
  }

  /**
   * Get attribute value
   * @param {string} key - Attribute key
   * @returns {*} Attribute value
   */
  getAttribute(key) {
    return this.attributes[key];
  }

  /**
   * Set attribute value
   * @param {string} key - Attribute key
   * @param {*} value - Attribute value
   */
  setAttribute(key, value) {
    this.attributes[key] = value;
    this.touch();
  }

  /**
   * Merge multiple attributes
   * @param {Object} attrs - Attributes to merge
   */
  setAttributes(attrs) {
    Object.assign(this.attributes, attrs);
    this.touch();
  }

  /**
   * Clone this geometry object
   * @returns {GeometryBase} Cloned object with new ID
   */
  clone() {
    throw new Error('clone() must be implemented by subclass');
  }

  /**
   * Transform this geometry (move, rotate, scale)
   * @param {Object} transform - Transform parameters
   * @returns {GeometryBase} Transformed geometry (new instance)
   */
  transform(transform) {
    throw new Error('transform() must be implemented by subclass');
  }

  /**
   * Get bounding box
   * @returns {{min: {x,y,z}, max: {x,y,z}}} Bounding box
   */
  getBoundingBox() {
    throw new Error('getBoundingBox() must be implemented by subclass');
  }

  /**
   * Serialize to JSON-compatible object
   * @returns {Object} Serialized geometry
   */
  toJSON() {
    return {
      id: this.id,
      type: this.type,
      attributes: { ...this.attributes },
      createdAt: this.createdAt,
      modifiedAt: this.modifiedAt,
    };
  }

  /**
   * Create geometry from JSON
   * @param {Object} json - Serialized geometry
   * @returns {GeometryBase} Geometry instance
   */
  static fromJSON(json) {
    throw new Error('fromJSON() must be implemented by subclass');
  }
}

export default GeometryBase;
